import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Typography, CircularProgress, Box } from '@material-ui/core';
import BigButtonNext from 'pages/reservationBookingOnlineV2/components/BigButtonNext';
import DesiredStoreOptions from 'pages/reservationBookingOnlineV2/components/DesiredStoreOptions';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import { stringify } from 'qs';
import moment from 'moment';
import TableCalender from 'pages/reservationBookingOnlineV2/components/TableCalender';
import SectionHeader from 'pages/reservationBookingOnlineV2/components/SectionHeader';
import { StatusBooking } from 'pages/reservationBookingOnlineV2/context';
import {
  ICalenderBooking, IDateBooking, IDateCalender, IStoreSettingDetail, IAvailableReservationBooking,
} from 'pages/reservationBookingOnlineV2/pages/StepDateAndTimeVisitSelection/types';
import { getAvailableReservationForNewClientsBookingOnlineApi, getStoreSettingDetailApi } from 'api/onlineBookingV2Api';
import { getTextGender } from 'constants/reservation';
import { MenuReselectState } from './MenuReselectPage';

const DAY_JA = ['日', '月', '火', '水', '木', '金', '土'];

const getIconByStatus = (status: keyof typeof StatusBooking): string => {
  switch (status) {
    case StatusBooking.AVAILABLE_BOOKING: return '◎';
    case StatusBooking.CALL_BEFORE_BOOKING: return '△';
    default: return '';
  }
};

const getClassByStatus = (status: keyof typeof StatusBooking): number => {
  switch (status) {
    case StatusBooking.AVAILABLE_BOOKING: return 0;
    case StatusBooking.CALL_BEFORE_BOOKING: return 1;
    default: return 2;
  }
};

function buildGridData(calendarData: IAvailableReservationBooking[]): {
  datesCalender: IDateCalender[];
  timesCalender: ICalenderBooking[][];
} {
  if (!calendarData.length) {
    return { datesCalender: [], timesCalender: [] };
  }

  const datesCalender: IDateCalender[] = calendarData.map((day) => {
    const d = moment(day.targetDate);
    return {
      dayOfWeek: `${d.format('M/DD')} (${DAY_JA[d.day()]})`,
      date: d.format('YYYY-MM-DD'),
      class: d.day(),
    };
  });

  const allTimes = Array.from(
    new Set(calendarData.flatMap((day) => day.workingCalendars.map((w) => w.time))),
  ).sort();

  const timesCalender: ICalenderBooking[][] = allTimes.map((time) => calendarData.map((day): ICalenderBooking => {
    const dateStr = moment(day.targetDate).format('YYYY-MM-DD');
    const slot = day.workingCalendars.find((w) => w.time === time);
    const status = (slot ? slot.status : 'CAN_NOT_BOOKING') as keyof typeof StatusBooking;
    const minutes = time.split(':')[1];
    const timeShort = minutes !== '00' ? minutes : time;
    return {
      key: `${dateStr}_${time}`,
      time,
      timeShort,
      date: dateStr,
      status,
      icon: getIconByStatus(status),
      class: getClassByStatus(status),
    };
  }));

  return { datesCalender, timesCalender };
}


const useStyles = makeStyles((theme: Theme) => createStyles({
  pageRoot: {
    width: '100%',
    minHeight: '100vh',
    backgroundColor: '#fff',
    fontFamily: 'Noto Sans JP',
    fontStyle: 'normal',
  },
  contentWrapper: {
    maxWidth: 980,
    marginTop: 30,
    marginBottom: 45,
    width: '95%',
    marginLeft: 'auto',
    marginRight: 'auto',
    [theme.breakpoints.down('sm')]: { marginTop: 8, marginBottom: 8 },
  },
  periodNote: {
    fontSize: '11px',
    color: '#555',
    padding: '0 10px 8px',
    lineHeight: 1.6,
  },
  neighborSection: {
    marginBottom: '30px',
  },
  boxBelt: {
    marginBottom: '0.6em',
    padding: '0.1em 0.6em',
    backgroundColor: '#F4F5F5',
    borderLeft: 'solid 10px #CD0021',
    color: '#000',
    fontSize: '26px',
    lineHeight: '39px',
    [theme.breakpoints.down('sm')]: { fontSize: '17px', lineHeight: '25px', letterSpacing: '0.28px' },
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    alignItems: 'center',
  },
  backLink: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    textDecoration: 'underline',
    textAlign: 'center' as const,
    display: 'block',
    marginTop: theme.spacing(1.5),
    fontSize: '0.9rem',
  },
}));

interface LocationState extends MenuReselectState {}

function MenuReselectDatePage() {
  const classes = useStyles();
  const location = useLocation<LocationState>();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const urlParams = new URLSearchParams(location.search);
  const token = urlParams.get('token') || '';
  const companyCode = urlParams.get('companyCode') || '';

  const prevState = location.state || {} as LocationState;
  const acceptRequestReservation = !!prevState.chosenCourseAcceptRequestReservation;

  const [weekStart, setWeekStart] = useState<moment.Moment>(() => moment().startOf('isoWeek'));
  const [selectedTime, setSelectedTime] = useState<ICalenderBooking | null>(null);
  const [calendarData, setCalendarData] = useState<IAvailableReservationBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrgId, setSelectedOrgId] = useState(prevState.chosenStoreId || '');
  const [storeSetting, setStoreSetting] = useState<IStoreSettingDetail>();

  // この画面は新規顧客用APIを呼ぶため、BE の getReservationBookingOnline(..., isNewCustomer=true)
  // と同じ式で判定する（新規顧客用が未登録なら既存顧客用にフォールバック）
  useEffect(() => {
    if (!selectedOrgId || !companyCode) return;
    getStoreSettingDetailApi({ orgId: selectedOrgId, companyCode })
      .then((res) => setStoreSetting({
        ...res,
        possibleMonthCode: res?.newCustomerPossibleMonthCode ?? res?.possibleMonthCode,
      }))
      .catch(() => setStoreSetting(undefined));
  }, [selectedOrgId, companyCode]);

  useEffect(() => {
    if (!prevState.chosenCourseId || !selectedOrgId) {
      setLoading(false);
      return;
    }
    const startDate = weekStart.format('YYYY-MM-DD');
    const endDate = weekStart.clone().add(6, 'days').format('YYYY-MM-DD');
    setLoading(true);
    getAvailableReservationForNewClientsBookingOnlineApi({
      companyCode,
      startDate,
      endDate,
      classType: getTextGender(prevState.classType || prevState.chosenServiceId),
      contractCourseCategoryId: prevState.chosenCareId,
      contractCourseId: prevState.chosenCourseId,
      areaId: prevState.chosenAreaId,
      orgId: selectedOrgId,
      reservationId: prevState.reservationId, // R-4: 編集中予約 ID を BE に伝搬し cross-pool / booked から自身除外
    })
      .then((res) => setCalendarData(res))
      .catch(() => {
        enqueueSnackbar('予約枠の取得に失敗しました。', { variant: 'error' });
        setCalendarData([]);
      })
      .finally(() => setLoading(false));
  }, [companyCode, weekStart, selectedOrgId, prevState.chosenCourseId, prevState.classType,
    prevState.chosenServiceId, prevState.chosenCareId, prevState.chosenAreaId, enqueueSnackbar]);

  const { datesCalender, timesCalender } = useMemo(() => buildGridData(calendarData), [calendarData]);

  const dateBooking: IDateBooking = {
    startDate: weekStart.format('YYYY-MM-DD'),
    endDate: weekStart.clone().add(6, 'days').format('YYYY-MM-DD'),
  };

  const handleSubmit = () => {
    if (!selectedTime) {
      enqueueSnackbar('日時を選択してください', { variant: 'warning' });
      return;
    }
    const selectedDatetime = `${selectedTime.date}T${selectedTime.time}:00`;
    history.push(
      `/contact/reservationMenuReselectConfirm?${stringify({ token, companyCode })}`,
      { ...prevState, selectedDatetime },
    );
  };

  const handleBack = () => {
    history.replace(
      `/contact/reservationMenuReselect?${stringify({ token, companyCode })}`,
    );
  };

  const periodDate = moment().add(2, 'days');
  const periodNote = `※${periodDate.format('YYYY年MM月DD日')}（${DAY_JA[periodDate.day()]}）以降でお申し込みいただけます。当日のご予約は、お電話にてお願い致します。`;

  return (
    <div className={classes.pageRoot}>
      <div className={classes.contentWrapper}>
        <SectionHeader title={acceptRequestReservation ? '2. ご来店希望日時を選択' : '2. ご来店日時を選択'} />

        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <TableCalender
            storeSetting={storeSetting}
            storeName={prevState.chosenStoreName || ''}
            timesCalender={timesCalender}
            datesCalender={datesCalender}
            dateBooking={dateBooking}
            handleChangePrevWeek={() => {
              setWeekStart((prev) => moment(prev).subtract(7, 'days'));
              setSelectedTime(null);
            }}
            handleChangeNextWeek={() => {
              setWeekStart((prev) => moment(prev).add(7, 'days'));
              setSelectedTime(null);
            }}
            handleChangeSelectTime={(item) => setSelectedTime(item)}
            handleChangeSelectDate={(date) => {
              setWeekStart(moment(date).startOf('isoWeek'));
              setSelectedTime(null);
            }}
            disableNonBookableSlot={true}
          />
        )}

        <Typography className={classes.periodNote}>
          {periodNote}
        </Typography>

        {prevState.areaStoreList && prevState.areaStoreList.length > 1 && (
          <div className={classes.neighborSection}>
            <h4 className={classes.boxBelt}>近隣のサロンの予約状況を見る</h4>
            <DesiredStoreOptions
              list={prevState.areaStoreList}
              defaultOption={selectedOrgId}
              handleClick={(id) => {
                setSelectedOrgId(id);
                setSelectedTime(null);
              }}
            />
          </div>
        )}

        <div className={classes.buttonContainer}>
          <BigButtonNext content="予約を変更する" handleSubmit={handleSubmit} isDisabled={!selectedTime} />
          <Typography
            component="span"
            className={classes.backLink}
            onClick={handleBack}
          >
            戻る
          </Typography>
        </div>
      </div>
    </div>
  );
}

export default MenuReselectDatePage;
